import { useState, useContext } from "react";
import { getFirstAccessibleRoute } from "./utils/getFirstAccessibleRoute";
import { Route, Routes, BrowserRouter as Router, Navigate, useLocation } from "react-router-dom";
import "./App.css";

import ToastMsg, { ToastMsgInit } from "./components/common/ToastMsg";
import { ToastProvider, useToast } from "./context/ToastContext";

// auth imports
import Login from "./pages/auth/Login";

// layout import
import ProtectedRoute from "./routes/ProtectedRoute";
import { AppLayout } from "./components/layout/app-layout";
import { SessionProvider, AuthContext } from "./context/AuthContext";
import { ThemeProvider } from "./components/themeProvider";
import { ConfigProvider } from "antd";

// dashboard
import Dashboard from "./pages/dashboard/Dashboard";

// boundary handler imports
import { NetworkError, NetworkStatusHandler } from "./pages/boundary/NetworkStatus";
import VersionGate from "./components/common/VersionGate";
import { SplashScreen } from "./pages/boundary/Splash";
import PageNotFound from "./pages/boundary/NotFound";
import CustomersList from "./pages/customers/CustomersList";
import CustomerDetails from "./pages/customers/CustomerDetails";
import UsersList from "./pages/users/UsersList";
import RolesManager from "./pages/settings/RolesManager";
import CustomerTypeMaster from "./pages/settings/CustomerTypeMaster";
import ModulesMaster from "./pages/settings/ModulesMaster";
import CustomFields from "./pages/settings/CustomFields";
import ImplementationApprovals from "./pages/settings/ImplementationApprovals";
import MOUSettings from "./pages/settings/MOUSettings";
import PackagesMaster from "./pages/settings/PackagesMaster";
import QuotationTemplateEditor from "./pages/settings/QuotationTemplateEditor";
import ProductPricing from "./pages/settings/ProductPricing";
import PlanServices from "./pages/settings/PlanServices";
import QuotationsList from "./pages/quotations/QuotationsList";
import QuotationComposer from "./pages/quotations/QuotationComposer";
import QuotationPrint from "./pages/quotations/QuotationPrint";
import QuotationMergedPrint from "./pages/quotations/QuotationMergedPrint";
import QuotationPublic from "./pages/quotations/QuotationPublic";
import ClientStatusPublic from "./pages/onboarded/ClientStatusPublic";
import MOUList from "./pages/mou/MOUList";
import MOUDetail from "./pages/mou/MOUDetail";
import MOUPublic from "./pages/mou/MOUPublic";
import MOUDashboard from "./pages/mou/MOUDashboard";
import QuotationKpi from "./pages/quotations/QuotationKpi";
import LeakAssumptions from "./pages/settings/LeakAssumptions";
import ProjectsManager from "./pages/settings/ProjectsManager";
import ReleasesManager from "./pages/settings/ReleasesManager";
import HospitalProfileConfig from "./pages/settings/HospitalProfileConfig";
import CREHandoverConfig from "./pages/settings/CREHandoverConfig";
import StageRequiredFieldsConfig from "./pages/settings/StageRequiredFieldsConfig";
import DuplicateRules from "./pages/settings/DuplicateRules";
import LeadPriority from "./pages/settings/LeadPriority";
import OutboundSettings from "./pages/settings/OutboundSettings";
import OutboundOverview from "./pages/outbound/OutboundOverview";
import OutboundProspects from "./pages/outbound/Prospects";
import OutboundSequences from "./pages/outbound/Sequences";
import OutboundOutbox from "./pages/outbound/Outbox";
import OutboundReplies from "./pages/outbound/Replies";
import LeadAssignment from "./pages/settings/LeadAssignment";
import UnassignedLeads from "./pages/customers/UnassignedLeads";
import LeadAssignmentReport from "./pages/reports/LeadAssignmentReport";
import LeadWelcomeReport from "./pages/reports/LeadWelcomeReport";
import DemoReport from "./pages/reports/DemoReport";
import NurtureReport from "./pages/reports/NurtureReport";
import CityStateMapping from "./pages/settings/CityStateMapping";
import CompanyResearchSettings from "./pages/settings/CompanyResearchSettings";
import SalesAgentOverview from "./pages/sales-agent/SalesAgentOverview";
import BriefingHistory from "./pages/sales-agent/BriefingHistory";
import SalesAgentSettings from "./pages/sales-agent/SalesAgentSettings";
import DailyEmailSettings from "./pages/sales-agent/DailyEmailSettings";
import TimelineReports from "./pages/reports/TimelineReport";
import AISuggestions from "./pages/reports/AISuggestions";
import TeamPerformanceReport from "./pages/reports/TeamPerformanceReport";
import DemoDesk from "./pages/reports/DemoDesk";
import ActionCenter from "./pages/reports/ActionCenter";
import Coaching from "./pages/reports/Coaching";
import PipelineActivityReport from "./pages/reports/PipelineActivityReport";
import FollowUp from "./pages/users/FollowUp";
import TasksList from "./pages/tasks/TasksList";
import DemoCalendar from "./pages/demos/DemoCalendar";
import ReportIndex from "./pages/reports/ReportIndex";
import UnifiedReport from "./pages/reports/unified/UnifiedReport";

// Engage pages that only ever existed on platform-core-extract. The rest of
// the Engage imports live in the block further down (the ai-voice-agent-b-dup
// implementation, which is the one we standardised on during the merge).
import PatientsList from "./pages/engage/PatientsList";
import PatientDetail from "./pages/engage/PatientDetail";
import ReferralsList from "./pages/engage/ReferralsList";
import EngageAnalytics from "./pages/engage/EngageAnalytics";

// funnel category imports
import FunnelCategory from "./pages/funnel/FunnelCategory";
import FunnelCategoryDetails from "./pages/funnel/FunnelCategoryDetails";
import LeadSourcesReport from "./pages/reports/LeadSourcesReport";
import MetaPerformanceReport from "./pages/reports/MetaPerformanceReport";
import LeadTypeReport from "./pages/reports/LeadTypeReport";
import LeadStageReport from "./pages/reports/LeadStageReport";
import ChannelPartnerReport from "./pages/reports/ChannelPartnerReport";
import LeadPotentialReport from "./pages/reports/LeadPotentialReport";
import SalesForecastingReport from "./pages/reports/SalesForecastingReport";
import ForecastConfig from "./pages/settings/ForecastConfig";
import ZapierWebhook from "./pages/settings/ZapierWebhook";
import EmailIntegration from "./pages/settings/EmailIntegration";
import EmailTemplates from "./pages/settings/EmailTemplates";
import EmailAnalyticsReport from "./pages/reports/EmailAnalyticsReport";
import EngagementReport from "./pages/reports/EngagementReport";
import ConversionAnalyticsReport from "./pages/reports/ConversionAnalyticsReport";
import AdPerformanceReport from "./pages/reports/AdPerformanceReport";
import AdLiveDashboard from "./pages/reports/AdLiveDashboard";
import AdCreativeReport from "./pages/reports/AdCreativeReport";
import AdSpendPacingReport from "./pages/reports/AdSpendPacingReport";
import AdBreakdownReport from "./pages/reports/AdBreakdownReport";
import AdProjectRoiReport from "./pages/reports/AdProjectRoiReport";
import LeadAttributionReport from "./pages/reports/LeadAttributionReport";
import AdFunnelReport from "./pages/reports/AdFunnelReport";
import AdLeadQualityReport from "./pages/reports/AdLeadQualityReport";
import RepAdEffectivenessReport from "./pages/reports/RepAdEffectivenessReport";
import AdTimeLagReport from "./pages/reports/AdTimeLagReport";
import AdConversionsReport from "./pages/reports/AdConversionsReport";
import MetaAdsIntegration from "./pages/settings/MetaAdsIntegration";
import Telephony from "./pages/settings/Telephony";
import TelephonyCallLog from "./pages/telephony/CallLog";
import WhatsAppIntegration from "./pages/settings/WhatsAppIntegration";
import WhatsAppTemplates from "./pages/settings/WhatsAppTemplates";
import WhatsAppCampaignList from "./pages/whatsapp/WhatsAppCampaignList";
import WhatsAppCampaignComposer from "./pages/whatsapp/WhatsAppCampaignComposer";
import WhatsAppCampaignReport from "./pages/whatsapp/WhatsAppCampaignReport";
import WhatsAppInbox from "./pages/whatsapp/WhatsAppInbox";
import AuditLogReport from "./pages/reports/AuditLogReport";
import ERPIntegration from "./pages/settings/ERPIntegration";
import ReferralProgramSettings from "./pages/settings/ReferralProgramSettings";
import ReferralSyncStatus from "./pages/settings/ReferralSyncStatus";
import ReferralRewards from "./pages/customers/ReferralRewards";

// Onboarded clients
import OnboardedList            from "./pages/onboarded/OnboardedList";
import OnboardedDetail          from "./pages/onboarded/OnboardedDetail";
import AMCTrackerReport         from "./pages/onboarded/AMCTrackerReport";
import OnboardedRevenueReport   from "./pages/onboarded/OnboardedRevenueReport";
import OnboardedClientListReport from "./pages/onboarded/OnboardedClientListReport";
import ImplementationStatusReport from "./pages/onboarded/ImplementationStatusReport";

// AI Caller
import AICallerDashboard from "./pages/ai-caller/AICallerDashboard";
import AICallerCallLogs from "./pages/ai-caller/AICallerCallLogs";
import AICallerCallDetail from "./pages/ai-caller/AICallerCallDetail";
import AICallerCampaigns from "./pages/ai-caller/AICallerCampaigns";
import AICallerSettings from "./pages/ai-caller/AICallerSettings";
import AICallerBucket from "./pages/ai-caller/AICallerBucket";

// Admin / Onboarding portal
import AdminLogin     from "./pages/admin/AdminLogin";
import AdminDashboard from "./pages/admin/AdminDashboard";
import CreateClient   from "./pages/admin/CreateClient";
import ClientDetail   from "./pages/admin/ClientDetail";
import ManagePlans    from "./pages/admin/ManagePlans";

// Engage (Hospital CRM) pages
import EngageDashboard from "./pages/engage/EngageDashboard";
import EnquiryBulkImport from "./pages/engage/EnquiryBulkImport";
import EnquiriesList from "./pages/engage/EnquiriesList";
import EnquiryDetail from "./pages/engage/EnquiryDetail";
import PatientEngagementDashboard from "./pages/engage/PatientEngagementDashboard";
import NewEnquiry from "./pages/engage/NewEnquiry";
import ReferringDoctorsList from "./pages/engage/ReferringDoctorsList";
import ReferringDoctorDetail from "./pages/engage/ReferringDoctorDetail";
import NewReferringDoctor from "./pages/engage/NewReferringDoctor";
import AmbulanceProvidersList from "./pages/engage/AmbulanceProvidersList";
import AmbulanceProviderDetail from "./pages/engage/AmbulanceProviderDetail";
import NewAmbulanceProvider from "./pages/engage/NewAmbulanceProvider";
import CorporateAccountsList from "./pages/engage/CorporateAccountsList";
import CorporateAccountDetail from "./pages/engage/CorporateAccountDetail";
import NewCorporateAccount from "./pages/engage/NewCorporateAccount";
import HealthCampsList from "./pages/engage/HealthCampsList";
import HealthCampDetail from "./pages/engage/HealthCampDetail";
import NewHealthCamp from "./pages/engage/NewHealthCamp";
import DigitalMarketingList from "./pages/engage/DigitalMarketingList";
import DigitalMarketingDetail from "./pages/engage/DigitalMarketingDetail";
import NewDigitalMarketing from "./pages/engage/NewDigitalMarketing";
import FollowupsList from "./pages/engage/FollowupsList";
import ReportsHub from "./pages/engage/reports/ReportsHub";
import EngageReportsDashboard from "./pages/engage/reports/ReportsDashboard";
import EnquiryFunnelReport from "./pages/engage/reports/EnquiryFunnelReport";
import ReferrerLeaderboardReport from "./pages/engage/reports/ReferrerLeaderboardReport";
import HealthCampROIReport from "./pages/engage/reports/HealthCampROIReport";
import SourceROIReport from "./pages/engage/reports/SourceROIReport";
import DigitalMarketingROIReport from "./pages/engage/reports/DigitalMarketingROIReport";
import EngageMetaPerformanceReport from "./pages/engage/reports/MetaPerformanceReport";
import NoShowRateReport from "./pages/engage/reports/NoShowRateReport";
import FollowupCompletionReport from "./pages/engage/reports/FollowupCompletionReport";
import EngagementDistributionReport from "./pages/engage/reports/EngagementDistributionReport";
import EngageConversionAnalyticsReport from "./pages/engage/reports/ConversionAnalyticsReport";
import EngageTimelineReport from "./pages/engage/reports/TimelineReport";
import CorporateRenewalsReport from "./pages/engage/reports/CorporateRenewalsReport";
import RevenueSourceReport from "./pages/engage/reports/RevenueSourceReport";
import EngageAICallerCallLogs from "./pages/engage/ai-caller/EngageAICallerCallLogs";
import EngageAICallerCallDetail from "./pages/engage/ai-caller/EngageAICallerCallDetail";
import EngageAICallerBucket from "./pages/engage/ai-caller/EngageAICallerBucket";
import EngageAICallerCampaigns from "./pages/engage/ai-caller/EngageAICallerCampaigns";
import EngageAICallerSettings from "./pages/engage/ai-caller/EngageAICallerSettings";
import EngageProcedureCatalogSettings from "./pages/engage/settings/ProcedureCatalogSettings";
import EngageDepartmentARPPSettings from "./pages/engage/settings/DepartmentARPPSettings";
import EngageUsersSettings from "./pages/engage/settings/UsersSettings";
import EngageReferralSourcesSettings from "./pages/engage/settings/ReferralSourcesSettings";
import EngagePatientTypesSettings from "./pages/engage/settings/PatientTypesSettings";
import EngageAssignmentSettings from "./pages/engage/settings/AssignmentSettings";
import EngageEmedhubLinkSettings from "./pages/engage/settings/EmedhubLinkSettings";

// Partner Portal (referring-doctors + ambulance partners self-service)
import PartnerPortalSignin from "./pages/partner-portal/PartnerPortalSignin";
import PartnerPortalLayout from "./pages/partner-portal/PartnerPortalLayout";
import PartnerPortalDashboard from "./pages/partner-portal/PartnerPortalDashboard";
import PartnerPortalReferralsList from "./pages/partner-portal/PartnerPortalReferralsList";
import PartnerPortalNewReferral from "./pages/partner-portal/PartnerPortalNewReferral";
import PartnerPortalPickupsList from "./pages/partner-portal/PartnerPortalPickupsList";
import PartnerPortalNewPickup from "./pages/partner-portal/PartnerPortalNewPickup";
import EmailCampaignList from "./pages/email/EmailCampaignList";
import EmailCampaignComposer from "./pages/email/EmailCampaignComposer";
import EmailCampaignReport from "./pages/email/EmailCampaignReport";
import SendingPool from "./pages/email/SendingPool";


// Permission-based route guard
// `module` can be a string or an array of strings (any match = allowed)
const PermissionRoute = ({ module, children }) => {
  const { hasPermission } = useContext(AuthContext);
  const location = useLocation();
  const modules = Array.isArray(module) ? module : [module];
  const allowed = modules.some(m => hasPermission(m, "view"));
  if (!allowed) {
    // Never redirect to where we already are. getFirstAccessibleRoute can name
    // a guarded route, and bouncing to it from that same route is an infinite
    // loop that presents as a blank, data-less page rather than a refusal.
    const target = getFirstAccessibleRoute();
    if (target === location.pathname) {
      return <Navigate to="/no-access" replace />;
    }
    return <Navigate to={target} replace />;
  }
  return children;
};

// A page that only some organisations' plans include (hasFeature).
const FeatureRoute = ({ feature, children }) => {
  const { hasFeature } = useContext(AuthContext);
  // There is no bare /settings route, so sending users there showed "page not found".
  return hasFeature(feature) ? children : <Navigate to="/no-access" replace />;
};

// super_admin only route guard
const SuperAdminRoute = ({ children }) => {
  const role = sessionStorage.getItem("login_role");
  if (role !== "super_admin") {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

// Admin + super_admin route guard — for permission administration
// (roles & users), which both full-access roles may manage.
const AdminRouteGuard = ({ children }) => {
  const role = sessionStorage.getItem("login_role");
  if (role !== "super_admin" && role !== "admin") {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

// Onboarding portal guard — requires admin_token in sessionStorage
const AdminRoute = ({ children }) => {
  const token = sessionStorage.getItem("admin_token");
  if (!token) return <Navigate to="/admin/login" replace />;
  return children;
};

// Engage-tenant route guard — gates the entire /engage/* tree.
// Tenants configured with product_type='engage' see Engage; everyone else
// is bounced to their normal TrustEdge landing page.
const EngageProductRoute = ({ module, children }) => {
  const { isEngageTenant, hasPermission } = useContext(AuthContext);
  if (!isEngageTenant()) {
    return <Navigate to={getFirstAccessibleRoute()} replace />;
  }
  // When a module is specified, the role must have its view permission.
  // super_admin/admin bypass via hasPermission.
  if (module && !hasPermission(module, "view")) {
    return <Navigate to={getFirstAccessibleRoute()} replace />;
  }
  return children;
};

// Feature-gate wrapper for Engage routes — checks the tenant's plan feature keys.
// If the plan doesn't include this feature, redirect to the Engage dashboard.
const EngageFeatureRoute = ({ feature, module, children }) => {
  const { isEngageTenant, hasFeature, hasPermission } = useContext(AuthContext);
  if (!isEngageTenant()) {
    return <Navigate to={getFirstAccessibleRoute()} replace />;
  }
  if (!hasFeature(feature)) {
    return <Navigate to="/engage/dashboard" replace />;
  }
  // When a module is specified, the role must also have its view permission.
  if (module && !hasPermission(module, "view")) {
    return <Navigate to={getFirstAccessibleRoute()} replace />;
  }
  return children;
};

// Terminal landing for a user whose role has no accessible pages — avoids the
// redirect loop that a guarded fallback would cause.
const NoAccess = () => (
  <div className="flex min-h-[60vh] flex-col items-center justify-center gap-2 text-center px-4">
    <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">No pages assigned</h2>
    <p className="max-w-md text-sm text-gray-500 dark:text-gray-400">
      Your account doesn&apos;t have access to any pages yet. Please contact your administrator to be assigned a role with page access.
    </p>
  </div>
);


/**
 * Route guard for Engage settings pages. Redirects to the Engage dashboard
 * when the user isn't a super_admin and lacks the required module permission.
 * Falls through to the child route when allowed.
 */
const EngageSettingsRoute = ({ module = 'settings', children }) => {
  const { isEngageTenant, hasPermission } = useContext(AuthContext);
  if (!isEngageTenant()) return <Navigate to={getFirstAccessibleRoute()} replace />;
  const isSuperAdmin = sessionStorage.getItem('login_role') === 'super_admin';
  if (!isSuperAdmin && !hasPermission(module, 'view')) {
    ToastMsg('error', "You don't have permission to access that settings page.");
    return <Navigate to="/engage/dashboard" replace />;
  }
  return children;
};


// Wrapper to initialize global ToastMsg
const AppWrapper = () => {
  const { showToast } = useToast();
  ToastMsgInit(showToast);

  return (
    <Router>
      <NetworkStatusHandler />
      {/* Reloads a tab still running a build we have already replaced. */}
      <VersionGate />
      <Routes>
        {/* Auth routes */}
        <Route path="/" element={<Login />} />

        {/* Public client-facing quotation */}
        <Route path="/q/:token" element={<QuotationPublic />} />

        {/* Public client-facing implementation status. The tenant slug is in
            the path because this route carries no JWT for the backend to
            resolve a tenant database from. */}
        <Route path="/status/:slug/:token" element={<ClientStatusPublic />} />

        {/* Public client-facing MOU. Same slug-in-the-path reason as above:
            there is no JWT on this route for the backend to resolve a tenant
            database from. */}
        <Route path="/mou/:slug/:token" element={<MOUPublic />} />

        {/* Demo-time KPI window. Authenticated and permission-gated like
            everything else, but deliberately outside AppLayout: it is opened
            in its own window and shown to the hospital, where the sidebar and
            header are only clutter. */}
        <Route path="/quotations/:id/kpi" element={
          <ProtectedRoute>
            <PermissionRoute module="quotations"><QuotationKpi /></PermissionRoute>
          </ProtectedRoute>
        } />

        {/* Print pages. Outside AppLayout for the same reason, and one more:
            the shell is a screen-high box that scrolls inside itself
            (SidebarInset h-svh + overflow-y-auto), and a browser prints such a
            box as one screen — every page after the first was cut off. Here
            the document is the page, so it flows onto as many sheets as it has. */}
        <Route path="/quotations/:id/print" element={
          <ProtectedRoute>
            <PermissionRoute module="quotations"><QuotationPrint /></PermissionRoute>
          </ProtectedRoute>
        } />
        <Route path="/quotations/merged" element={
          <ProtectedRoute>
            <PermissionRoute module="quotations"><QuotationMergedPrint /></PermissionRoute>
          </ProtectedRoute>
        } />

        {/* Partner Portal (referring doctors + ambulance partners) */}
        <Route path="/partner-portal/login" element={<PartnerPortalSignin />} />
        <Route path="/partner-portal" element={<PartnerPortalLayout />}>
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard"       element={<PartnerPortalDashboard />} />
          <Route path="referrals"       element={<PartnerPortalReferralsList />} />
          <Route path="referrals/new"   element={<PartnerPortalNewReferral />} />
          <Route path="pickups"         element={<PartnerPortalPickupsList />} />
          <Route path="pickups/new"     element={<PartnerPortalNewPickup />} />
        </Route>

{/* Protected routes */}
        <Route
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        >
          {/* Engage (Hospital CRM) — gated by tenant product_type */}
          <Route path="/engage"            element={<EngageProductRoute><EngageDashboard /></EngageProductRoute>} />
          <Route path="/engage/dashboard"  element={<EngageProductRoute><EngageDashboard /></EngageProductRoute>} />
          <Route path="/engage/enquiries"      element={<EngageProductRoute><EnquiriesList  /></EngageProductRoute>} />
          <Route path="/engage/enquiries/:id"  element={<EngageProductRoute><EnquiryDetail /></EngageProductRoute>} />
          <Route path="/engage/patients"       element={<EngageProductRoute><PatientsList   /></EngageProductRoute>} />
          <Route path="/engage/patients/:id"   element={<EngageProductRoute><PatientDetail  /></EngageProductRoute>} />
          <Route path="/engage/followups"          element={<EngageProductRoute><FollowupsList         /></EngageProductRoute>} />
          <Route path="/engage/referring-doctors"  element={<EngageProductRoute><ReferringDoctorsList /></EngageProductRoute>} />
          <Route path="/engage/referrals"          element={<EngageProductRoute><ReferralsList         /></EngageProductRoute>} />
          <Route path="/engage/camps"              element={<EngageProductRoute><HealthCampsList       /></EngageProductRoute>} />
          <Route path="/engage/corporates"         element={<EngageProductRoute><CorporateAccountsList /></EngageProductRoute>} />
          <Route path="/engage/analytics"          element={<EngageProductRoute><EngageAnalytics      /></EngageProductRoute>} />

          {/* Always accessible after login */}
          {/* Ungated on purpose — see the note in use-navigation.js. The data is
              scoped to the caller server-side, so there is nothing here a user
              could see that they cannot already see on the leads list. */}
          <Route path="/dashboard" element={<Dashboard />} />

          {/* Customers module */}
          <Route path="/customers" element={<PermissionRoute module="customers"><CustomersList key="prospect" defaultCategory="prospect" /></PermissionRoute>} />
          <Route path="/customers/customerdetails" element={<PermissionRoute module="customers"><CustomerDetails /></PermissionRoute>} />
          {/* Lead assignment queue: managers assign, reps take leads when allowed (backend decides) */}
          <Route path="/customers/unassigned" element={<PermissionRoute module={['lead_reassign', 'customers']}><UnassignedLeads /></PermissionRoute>} />

          {/* Channel Partners module */}
          <Route path="/channel-partners" element={<PermissionRoute module="channel_partners"><CustomersList key="channel_partner" defaultCategory="channel_partner" /></PermissionRoute>} />

          {/* MOU — the stage between a won deal and an onboarded client */}
          <Route path="/mou"     element={<PermissionRoute module="mou"><MOUList /></PermissionRoute>} />
          <Route path="/mou/dashboard" element={<PermissionRoute module="mou"><MOUDashboard /></PermissionRoute>} />
          <Route path="/mou/:id" element={<PermissionRoute module="mou"><MOUDetail /></PermissionRoute>} />

          {/* Onboarded Clients module */}
          <Route path="/onboarded"                           element={<PermissionRoute module="onboarded"><OnboardedList /></PermissionRoute>} />
          <Route path="/onboarded/:id"                       element={<PermissionRoute module="onboarded"><OnboardedDetail /></PermissionRoute>} />
          <Route path="/onboarded/reports/amc-tracker"       element={<PermissionRoute module="onboarded"><AMCTrackerReport /></PermissionRoute>} />
          <Route path="/onboarded/reports/revenue"           element={<PermissionRoute module="onboarded"><OnboardedRevenueReport /></PermissionRoute>} />
          <Route path="/onboarded/reports/client-list"       element={<PermissionRoute module="onboarded"><OnboardedClientListReport /></PermissionRoute>} />
          <Route path="/onboarded/reports/implementation"    element={<PermissionRoute module="onboarded"><ImplementationStatusReport /></PermissionRoute>} />

          {/* Follow-up module */}
          <Route path="/followup" element={<PermissionRoute module="followup"><FollowUp /></PermissionRoute>} />
          <Route path="/tasks"    element={<PermissionRoute module="tasks"><TasksList /></PermissionRoute>} />

          {/* Demos */}
          <Route path="/demos"          element={<PermissionRoute module="followup"><DemoCalendar /></PermissionRoute>} />
          <Route path="/demos/calendar" element={<PermissionRoute module="followup"><DemoCalendar /></PermissionRoute>} />

          {/* Reports module */}
          <Route path="/reports"                  element={<PermissionRoute module={['report_timeline','report_lead_sources','report_lead_types','report_lead_potential','report_sales_forecast','report_engagement','report_conversion','report_email_analytics','report_pipeline_activity']}><ReportIndex /></PermissionRoute>} />
          {/* All-in-One report — visible to anyone who can see at least one report */}
          <Route path="/reports/all"              element={<PermissionRoute module={['report_timeline','report_lead_sources','report_lead_types','report_lead_potential','report_sales_forecast','report_engagement','report_conversion','report_email_analytics','report_pipeline_activity','onboarded']}><UnifiedReport /></PermissionRoute>} />
          <Route path="/reports/timeline"         element={<PermissionRoute module="report_timeline"><TimelineReports /></PermissionRoute>} />
          {/* AI next actions + daily manager briefing (written by the local AI worker) */}
          <Route path="/reports/ai-suggestions"   element={<PermissionRoute module="report_timeline"><AISuggestions /></PermissionRoute>} />
          {/* Sales team performance — every rep side by side, with monthly targets */}
          <Route path="/reports/team-performance" element={<PermissionRoute module="report_timeline"><TeamPerformanceReport /></PermissionRoute>} />
          {/* Demo no-show prevention + win/loss coaching */}
          {/* Where a notification click lands: live numbers behind every alert */}
          <Route path="/reports/action-center"    element={<PermissionRoute module={["report_timeline", "sales_agent"]}><ActionCenter /></PermissionRoute>} />
          <Route path="/reports/demo-desk"        element={<PermissionRoute module="report_timeline"><DemoDesk /></PermissionRoute>} />
          <Route path="/reports/coaching"         element={<PermissionRoute module="report_timeline"><Coaching /></PermissionRoute>} />
          <Route path="/reports/pipeline-activity" element={<PermissionRoute module="report_pipeline_activity"><PipelineActivityReport /></PermissionRoute>} />
          <Route path="/reports/lead-sources"     element={<PermissionRoute module="report_lead_sources"><LeadSourcesReport /></PermissionRoute>} />
          <Route path="/reports/meta-performance" element={<PermissionRoute module="report_lead_sources"><MetaPerformanceReport /></PermissionRoute>} />
          <Route path="/reports/lead-types"       element={<PermissionRoute module="report_lead_types"><LeadTypeReport /></PermissionRoute>} />
          <Route path="/reports/lead-stages"      element={<PermissionRoute module="report_lead_stages"><LeadStageReport /></PermissionRoute>} />
          <Route path="/reports/channel-partners" element={<PermissionRoute module="report_channel_partners"><ChannelPartnerReport /></PermissionRoute>} />
          <Route path="/reports/lead-assignment"  element={<PermissionRoute module={['report_assignment', 'lead_assignment']}><LeadAssignmentReport /></PermissionRoute>} />
          <Route path="/reports/lead-potential"   element={<PermissionRoute module="report_lead_potential"><LeadPotentialReport /></PermissionRoute>} />
          <Route path="/reports/sales-forecast"   element={<PermissionRoute module="report_sales_forecast"><SalesForecastingReport /></PermissionRoute>} />
          <Route path="/reports/engagement"       element={<PermissionRoute module="report_engagement"><EngagementReport /></PermissionRoute>} />
          <Route path="/reports/lead-welcome"     element={<PermissionRoute module="report_engagement"><LeadWelcomeReport /></PermissionRoute>} />
          <Route path="/reports/demo-report"      element={<PermissionRoute module="report_engagement"><DemoReport /></PermissionRoute>} />
          <Route path="/reports/nurture"          element={<PermissionRoute module="report_engagement"><NurtureReport /></PermissionRoute>} />
          <Route path="/reports/conversion"       element={<PermissionRoute module="report_conversion"><ConversionAnalyticsReport /></PermissionRoute>} />
          <Route path="/reports/audit-log"        element={<SuperAdminRoute><AuditLogReport /></SuperAdminRoute>} />

          {/* Permission administration — admin + super_admin */}
          <Route path="/settings/users"              element={<AdminRouteGuard><UsersList /></AdminRouteGuard>} />
          <Route path="/settings/roles"              element={<AdminRouteGuard><RolesManager /></AdminRouteGuard>} />
          <Route path="/settings/customer-types"     element={<SuperAdminRoute><CustomerTypeMaster /></SuperAdminRoute>} />
          <Route path="/settings/modules"            element={<SuperAdminRoute><ModulesMaster /></SuperAdminRoute>} />
          {/* Admin-defined customer fields (dynamic attributes) */}
          <Route path="/settings/custom-fields"      element={<SuperAdminRoute><CustomFields /></SuperAdminRoute>} />
          <Route path="/settings/implementation-approvals" element={<PermissionRoute module="onboarded"><ImplementationApprovals /></PermissionRoute>} />
          <Route path="/settings/mou" element={<PermissionRoute module="mou"><MOUSettings /></PermissionRoute>} />
          <Route path="/settings/packages"           element={<SuperAdminRoute><PackagesMaster /></SuperAdminRoute>} />
          <Route path="/settings/quotation-template" element={<SuperAdminRoute><QuotationTemplateEditor /></SuperAdminRoute>} />
          <Route path="/settings/product-pricing"    element={<SuperAdminRoute><ProductPricing /></SuperAdminRoute>} />
          <Route path="/settings/plan-services"      element={<SuperAdminRoute><PlanServices /></SuperAdminRoute>} />
          <Route path="/settings/leak-assumptions"   element={<SuperAdminRoute><LeakAssumptions /></SuperAdminRoute>} />

          {/* Quotations */}
          <Route path="/quotations"                  element={<PermissionRoute module="quotations"><QuotationsList /></PermissionRoute>} />
          <Route path="/quotations/new"              element={<PermissionRoute module="quotations"><QuotationComposer /></PermissionRoute>} />
          <Route path="/quotations/:id"              element={<PermissionRoute module="quotations"><QuotationComposer /></PermissionRoute>} />
          <Route path="/settings/projects"           element={<SuperAdminRoute><ProjectsManager /></SuperAdminRoute>} />
          <Route path="/settings/releases"           element={<PermissionRoute module="releases"><ReleasesManager /></PermissionRoute>} />
          <Route path="/settings/hospital-profile"   element={<SuperAdminRoute><HospitalProfileConfig /></SuperAdminRoute>} />
          <Route path="/settings/cre-handover"       element={<SuperAdminRoute><CREHandoverConfig /></SuperAdminRoute>} />
          <Route path="/settings/mandatory-fields"   element={<SuperAdminRoute><StageRequiredFieldsConfig /></SuperAdminRoute>} />
          <Route path="/settings/duplicate-rules"    element={<SuperAdminRoute><DuplicateRules /></SuperAdminRoute>} />
          <Route path="/settings/lead-priority"      element={<SuperAdminRoute><LeadPriority /></SuperAdminRoute>} />
          <Route path="/settings/lead-assignment"    element={<PermissionRoute module={['lead_assignment', 'lead_reassign', 'settings']}><LeadAssignment /></PermissionRoute>} />
          <Route path="/settings/city-state"         element={<PermissionRoute module="city_state_map"><CityStateMapping /></PermissionRoute>} />
          <Route path="/settings/company-research"   element={<PermissionRoute module="company_research_settings"><FeatureRoute feature="company_research"><CompanyResearchSettings /></FeatureRoute></PermissionRoute>} />
          {/* Sales Manager Agent (trustedge sales_agent/); the Action Center is /reports/action-center */}
          <Route path="/sales-agent"            element={<PermissionRoute module="sales_agent"><FeatureRoute feature="sales_manager_agent"><SalesAgentOverview /></FeatureRoute></PermissionRoute>} />
          <Route path="/sales-agent/briefings"  element={<PermissionRoute module="sales_agent"><FeatureRoute feature="sales_manager_agent"><BriefingHistory /></FeatureRoute></PermissionRoute>} />
          <Route path="/sales-agent/settings"   element={<PermissionRoute module="sales_agent_settings"><FeatureRoute feature="sales_manager_agent"><SalesAgentSettings /></FeatureRoute></PermissionRoute>} />
          <Route path="/sales-agent/daily-email" element={<PermissionRoute module="sales_agent_settings"><FeatureRoute feature="sales_manager_agent"><DailyEmailSettings /></FeatureRoute></PermissionRoute>} />
          {/* Email campaigns — guarded by the single 'email' permission,
              same key the backend enforces on every email endpoint. */}
          <Route path="/email/sending-pool"        element={<PermissionRoute module="email"><SendingPool /></PermissionRoute>} />
          <Route path="/email/campaigns"            element={<PermissionRoute module="email"><EmailCampaignList /></PermissionRoute>} />
          <Route path="/email/campaigns/new"        element={<PermissionRoute module="email"><EmailCampaignComposer /></PermissionRoute>} />
          <Route path="/email/campaigns/:id/edit"   element={<PermissionRoute module="email"><EmailCampaignComposer /></PermissionRoute>} />
          <Route path="/email/campaigns/:id/report" element={<PermissionRoute module="email"><EmailCampaignReport /></PermissionRoute>} />
          <Route path="/settings/email-integration"  element={<PermissionRoute module="email"><EmailIntegration /></PermissionRoute>} />
          <Route path="/settings/email-templates"    element={<PermissionRoute module="email"><EmailTemplates /></PermissionRoute>} />
          <Route path="/reports/email-analytics"      element={<PermissionRoute module="report_email_analytics"><EmailAnalyticsReport /></PermissionRoute>} />
          <Route path="/reports/ad-performance"       element={<PermissionRoute module="report_ad_performance"><AdPerformanceReport /></PermissionRoute>} />
          <Route path="/reports/ad-dashboard"         element={<PermissionRoute module="report_ad_dashboard"><AdLiveDashboard /></PermissionRoute>} />
          <Route path="/reports/ad-creative"          element={<PermissionRoute module="report_ad_creative"><AdCreativeReport /></PermissionRoute>} />
          <Route path="/reports/ad-pacing"            element={<PermissionRoute module="report_ad_pacing"><AdSpendPacingReport /></PermissionRoute>} />
          {/* Audience and Geography are the same component over different dimensions */}
          <Route path="/reports/ad-audience"          element={<PermissionRoute module="report_ad_audience"><AdBreakdownReport mode="audience" /></PermissionRoute>} />
          <Route path="/reports/ad-geography"         element={<PermissionRoute module="report_ad_geography"><AdBreakdownReport mode="geography" /></PermissionRoute>} />
          <Route path="/reports/ad-roi"               element={<PermissionRoute module="report_ad_roi"><AdProjectRoiReport /></PermissionRoute>} />
          {/* These five need leads to carry an ad id; they render a warning until they do */}
          <Route path="/reports/ad-attribution"       element={<PermissionRoute module="report_ad_attribution"><LeadAttributionReport /></PermissionRoute>} />
          <Route path="/reports/ad-funnel"            element={<PermissionRoute module="report_ad_funnel"><AdFunnelReport /></PermissionRoute>} />
          <Route path="/reports/ad-lead-quality"      element={<PermissionRoute module="report_ad_lead_quality"><AdLeadQualityReport /></PermissionRoute>} />
          <Route path="/reports/ad-rep-effectiveness" element={<PermissionRoute module="report_ad_rep"><RepAdEffectivenessReport /></PermissionRoute>} />
          <Route path="/reports/ad-time-lag"          element={<PermissionRoute module="report_ad_time_lag"><AdTimeLagReport /></PermissionRoute>} />
          <Route path="/reports/ad-conversions"       element={<PermissionRoute module="report_ad_conversions"><AdConversionsReport /></PermissionRoute>} />
          <Route path="/settings/meta-ads"            element={<PermissionRoute module="meta_ads_settings"><MetaAdsIntegration /></PermissionRoute>} />
          <Route path="/settings/telephony"           element={<PermissionRoute module="telephony_settings"><Telephony /></PermissionRoute>} />
          <Route path="/telephony/calls"              element={<PermissionRoute module="telephony_settings"><TelephonyCallLog /></PermissionRoute>} />
          {/* Outbound prospecting (trustedge prospecting/) */}
          <Route path="/outbound"                     element={<PermissionRoute module="outbound"><OutboundOverview /></PermissionRoute>} />
          <Route path="/outbound/prospects"           element={<PermissionRoute module="outbound"><OutboundProspects /></PermissionRoute>} />
          <Route path="/outbound/sequences"           element={<PermissionRoute module="outbound"><OutboundSequences /></PermissionRoute>} />
          <Route path="/outbound/outbox"              element={<PermissionRoute module="outbound"><OutboundOutbox /></PermissionRoute>} />
          <Route path="/outbound/replies"             element={<PermissionRoute module="outbound"><OutboundReplies /></PermissionRoute>} />
          <Route path="/settings/outbound"            element={<PermissionRoute module="outbound_settings"><OutboundSettings /></PermissionRoute>} />
          <Route path="/settings/forecast-config"         element={<SuperAdminRoute><ForecastConfig /></SuperAdminRoute>} />
          <Route path="/settings/zapier-webhook"          element={<ZapierWebhook />} />
          <Route path="/settings/whatsapp-integration"    element={<PermissionRoute module="whatsapp"><WhatsAppIntegration /></PermissionRoute>} />
          <Route path="/settings/whatsapp-templates"      element={<PermissionRoute module="whatsapp"><WhatsAppTemplates /></PermissionRoute>} />
          <Route path="/settings/erp-integration"          element={<SuperAdminRoute><ERPIntegration /></SuperAdminRoute>} />
          <Route path="/settings/referral-program"        element={<PermissionRoute module="settings"><ReferralProgramSettings /></PermissionRoute>} />
          <Route path="/referral-rewards"                 element={<PermissionRoute module="customers"><ReferralRewards /></PermissionRoute>} />
          <Route path="/settings/referral-sync"           element={<PermissionRoute module="settings"><ReferralSyncStatus /></PermissionRoute>} />

          {/* AI Caller module */}
          <Route path="/ai-caller" element={<PermissionRoute module="ai_caller_dashboard"><AICallerDashboard /></PermissionRoute>} />
          <Route path="/ai-caller/calls" element={<PermissionRoute module="ai_caller_dashboard"><AICallerCallLogs /></PermissionRoute>} />
          <Route path="/ai-caller/calls/:id" element={<PermissionRoute module="ai_caller_dashboard"><AICallerCallDetail /></PermissionRoute>} />
          <Route path="/ai-caller/bucket" element={<PermissionRoute module="ai_caller_campaigns"><AICallerBucket /></PermissionRoute>} />
          <Route path="/ai-caller/campaigns" element={<PermissionRoute module="ai_caller_campaigns"><AICallerCampaigns /></PermissionRoute>} />
          <Route path="/settings/ai-caller" element={<PermissionRoute module="ai_caller_settings"><AICallerSettings /></PermissionRoute>} />

          {/* WhatsApp module */}
          <Route path="/whatsapp/campaigns"               element={<PermissionRoute module="whatsapp"><WhatsAppCampaignList /></PermissionRoute>} />
          <Route path="/whatsapp/campaigns/new"           element={<PermissionRoute module="whatsapp"><WhatsAppCampaignComposer /></PermissionRoute>} />
          <Route path="/whatsapp/campaigns/:id/edit"      element={<PermissionRoute module="whatsapp"><WhatsAppCampaignComposer /></PermissionRoute>} />
          <Route path="/whatsapp/campaigns/:id/report"    element={<PermissionRoute module="whatsapp"><WhatsAppCampaignReport /></PermissionRoute>} />
          <Route path="/whatsapp/inbox"                   element={<PermissionRoute module="whatsapp"><WhatsAppInbox /></PermissionRoute>} />

          {/* Funnel Category */}
          <Route path="/funnelcategory"                       element={<FunnelCategory />} />
          <Route path="/funnelcategory/funnelcategorydetails" element={<FunnelCategoryDetails />} />

          {/* Engage (Hospital CRM) — gated by product_type='engage' */}
          <Route path="/engage/dashboard"          element={<EngageProductRoute module="engage_dashboard"><EngageDashboard /></EngageProductRoute>} />
          <Route path="/engage/enquiries"          element={<EngageProductRoute module="engage_enquiries"><EnquiriesList /></EngageProductRoute>} />
          <Route path="/engage/enquiries/new"      element={<EngageProductRoute module="engage_enquiries"><NewEnquiry /></EngageProductRoute>} />
          <Route path="/engage/enquiries/:id"      element={<EngageProductRoute module="engage_enquiries"><EnquiryDetail /></EngageProductRoute>} />
          <Route path="/engage/enquiries/:id/convert"   element={<EngageProductRoute module="engage_enquiries"><EnquiryDetail /></EngageProductRoute>} />
          <Route path="/engage/enquiries/:id/engagement" element={<EngageProductRoute module="engage_enquiries"><PatientEngagementDashboard /></EngageProductRoute>} />
          <Route path="/engage/referring-doctors"        element={<EngageProductRoute module="engage_referring_doctors"><ReferringDoctorsList /></EngageProductRoute>} />
          <Route path="/engage/referring-doctors/new"     element={<EngageProductRoute module="engage_referring_doctors"><NewReferringDoctor /></EngageProductRoute>} />
          <Route path="/engage/referring-doctors/:id/edit" element={<EngageProductRoute module="engage_referring_doctors"><NewReferringDoctor /></EngageProductRoute>} />
          <Route path="/engage/referring-doctors/:id"     element={<EngageProductRoute module="engage_referring_doctors"><ReferringDoctorDetail /></EngageProductRoute>} />
          <Route path="/engage/ambulance-providers"       element={<EngageProductRoute module="engage_ambulance"><AmbulanceProvidersList /></EngageProductRoute>} />
          <Route path="/engage/ambulance-providers/new"   element={<EngageProductRoute module="engage_ambulance"><NewAmbulanceProvider /></EngageProductRoute>} />
          <Route path="/engage/ambulance-providers/:id/edit" element={<EngageProductRoute module="engage_ambulance"><NewAmbulanceProvider /></EngageProductRoute>} />
          <Route path="/engage/ambulance-providers/:id"   element={<EngageProductRoute module="engage_ambulance"><AmbulanceProviderDetail /></EngageProductRoute>} />
          <Route path="/engage/corporate-accounts"        element={<EngageProductRoute module="engage_corporate_accounts"><CorporateAccountsList /></EngageProductRoute>} />
          <Route path="/engage/corporate-accounts/new"    element={<EngageProductRoute module="engage_corporate_accounts"><NewCorporateAccount /></EngageProductRoute>} />
          <Route path="/engage/corporate-accounts/:id/edit" element={<EngageProductRoute module="engage_corporate_accounts"><NewCorporateAccount /></EngageProductRoute>} />
          <Route path="/engage/corporate-accounts/:id"    element={<EngageProductRoute module="engage_corporate_accounts"><CorporateAccountDetail /></EngageProductRoute>} />
          <Route path="/engage/health-camps"              element={<EngageProductRoute module="engage_health_camps"><HealthCampsList /></EngageProductRoute>} />
          <Route path="/engage/health-camps/new"          element={<EngageProductRoute module="engage_health_camps"><NewHealthCamp /></EngageProductRoute>} />
          <Route path="/engage/health-camps/:id/edit"     element={<EngageProductRoute module="engage_health_camps"><NewHealthCamp /></EngageProductRoute>} />
          <Route path="/engage/health-camps/:id"          element={<EngageProductRoute module="engage_health_camps"><HealthCampDetail /></EngageProductRoute>} />
          <Route path="/engage/digital-marketing"         element={<EngageProductRoute module="engage_digital_marketing"><DigitalMarketingList /></EngageProductRoute>} />
          <Route path="/engage/digital-marketing/new"     element={<EngageProductRoute module="engage_digital_marketing"><NewDigitalMarketing /></EngageProductRoute>} />
          <Route path="/engage/digital-marketing/:id/edit" element={<EngageProductRoute module="engage_digital_marketing"><NewDigitalMarketing /></EngageProductRoute>} />
          <Route path="/engage/digital-marketing/:id"     element={<EngageProductRoute module="engage_digital_marketing"><DigitalMarketingDetail /></EngageProductRoute>} />
          <Route path="/engage/followups"                          element={<EngageProductRoute module="engage_followups"><FollowupsList /></EngageProductRoute>} />
          <Route path="/engage/imports"                            element={<EngageProductRoute module="engage_bulk_import"><EnquiryBulkImport /></EngageProductRoute>} />

          {/* Engage Reports — one permission (engage_reports) covers the whole section */}
          {/* The unified dashboard is the Reports landing page; the per-report
              pages below remain for drill-down. The old tile hub moves to /hub. */}
          <Route path="/engage/reports"                            element={<EngageProductRoute module="engage_reports"><EngageReportsDashboard /></EngageProductRoute>} />
          <Route path="/engage/reports/hub"                        element={<EngageProductRoute module="engage_reports"><ReportsHub /></EngageProductRoute>} />
          <Route path="/engage/reports/enquiry-funnel"             element={<EngageProductRoute module="engage_reports"><EnquiryFunnelReport /></EngageProductRoute>} />
          <Route path="/engage/reports/referrer-leaderboard"       element={<EngageProductRoute module="engage_reports"><ReferrerLeaderboardReport /></EngageProductRoute>} />
          <Route path="/engage/reports/camp-roi"                   element={<EngageProductRoute module="engage_reports"><HealthCampROIReport /></EngageProductRoute>} />
          <Route path="/engage/reports/source-roi"                 element={<EngageProductRoute module="engage_reports"><SourceROIReport /></EngageProductRoute>} />
          <Route path="/engage/reports/marketing-roi"              element={<EngageProductRoute module="engage_reports"><DigitalMarketingROIReport /></EngageProductRoute>} />
          <Route path="/engage/reports/meta-performance" element={<EngageFeatureRoute feature="engage_reports" module="engage_reports"><EngageMetaPerformanceReport /></EngageFeatureRoute>} />
          <Route path="/engage/reports/no-show-rate"               element={<EngageProductRoute module="engage_reports"><NoShowRateReport /></EngageProductRoute>} />
          <Route path="/engage/reports/followup-completion"        element={<EngageProductRoute module="engage_reports"><FollowupCompletionReport /></EngageProductRoute>} />
          <Route path="/engage/reports/engagement"                 element={<EngageProductRoute module="engage_reports"><EngagementDistributionReport /></EngageProductRoute>} />
          <Route path="/engage/reports/conversion"                 element={<EngageProductRoute module="engage_reports"><EngageConversionAnalyticsReport /></EngageProductRoute>} />
          <Route path="/engage/reports/timeline"                   element={<EngageProductRoute module="engage_reports"><EngageTimelineReport /></EngageProductRoute>} />
          <Route path="/engage/reports/corporate-renewals"         element={<EngageProductRoute module="engage_reports"><CorporateRenewalsReport /></EngageProductRoute>} />
          <Route path="/engage/reports/revenue-source/:source"     element={<EngageProductRoute module="engage_reports"><RevenueSourceReport /></EngageProductRoute>} />
          <Route path="/engage/reports/*"                          element={<EngageProductRoute module="engage_reports"><ReportsHub /></EngageProductRoute>} />

          <Route path="/engage/ai-caller"                    element={<Navigate to="/engage/dashboard" replace />} />
          <Route path="/engage/ai-caller/dashboard"          element={<Navigate to="/engage/dashboard" replace />} />
          <Route path="/engage/ai-caller/calls"              element={<EngageFeatureRoute feature="engage_ai_caller" module="engage_ai_caller"><EngageAICallerCallLogs /></EngageFeatureRoute>} />
          <Route path="/engage/ai-caller/calls/:id"          element={<EngageFeatureRoute feature="engage_ai_caller" module="engage_ai_caller"><EngageAICallerCallDetail /></EngageFeatureRoute>} />
          <Route path="/engage/ai-caller/bucket"             element={<EngageFeatureRoute feature="engage_ai_caller" module="engage_ai_caller"><EngageAICallerBucket /></EngageFeatureRoute>} />
          <Route path="/engage/ai-caller/campaigns"          element={<EngageFeatureRoute feature="engage_ai_caller" module="engage_ai_caller"><EngageAICallerCampaigns /></EngageFeatureRoute>} />
          <Route path="/engage/ai-caller/settings"           element={<EngageFeatureRoute feature="engage_ai_caller" module="engage_ai_caller"><EngageAICallerSettings /></EngageFeatureRoute>} />
          <Route path="/engage/settings/procedure-catalog"   element={<EngageSettingsRoute module="settings"><EngageProcedureCatalogSettings /></EngageSettingsRoute>} />
          <Route path="/engage/settings/department-arpp"     element={<EngageSettingsRoute module="settings"><EngageDepartmentARPPSettings /></EngageSettingsRoute>} />
          <Route path="/engage/settings/users"               element={<EngageSettingsRoute module="users"><EngageUsersSettings /></EngageSettingsRoute>} />
          <Route path="/engage/settings/sources"             element={<EngageSettingsRoute module="settings"><EngageReferralSourcesSettings /></EngageSettingsRoute>} />
          <Route path="/engage/settings/assignment"          element={<EngageSettingsRoute module="settings"><EngageAssignmentSettings /></EngageSettingsRoute>} />
          <Route path="/engage/settings/patient-types"       element={<EngageSettingsRoute module="settings"><EngagePatientTypesSettings /></EngageSettingsRoute>} />
          <Route path="/engage/settings/emedhub"             element={<EngageSettingsRoute module="settings"><EngageEmedhubLinkSettings /></EngageSettingsRoute>} />
          <Route path="/engage/no-access"           element={<EngageProductRoute><NoAccess /></EngageProductRoute>} />
          {/* Terminal landing for an account with nothing granted — unguarded on purpose */}
          <Route path="/no-access"                  element={<NoAccess />} />
          <Route path="/engage/settings/*"         element={<EngageProductRoute><EngageDashboard /></EngageProductRoute>} />

          <Route path="/networkError" element={<NetworkError />} />
          <Route path="*" element={<PageNotFound />} />
        </Route>

        {/* Admin / Onboarding portal — outside ProtectedRoute */}
        <Route path="/admin/login"       element={<AdminLogin />} />
        <Route path="/admin/dashboard"   element={<AdminRoute><AdminDashboard /></AdminRoute>} />
        <Route path="/admin/clients/new" element={<AdminRoute><CreateClient /></AdminRoute>} />
        <Route path="/admin/clients/:id" element={<AdminRoute><ClientDetail /></AdminRoute>} />
        <Route path="/admin/plans"       element={<AdminRoute><ManagePlans /></AdminRoute>} />
      </Routes>
    </Router>
  );
};

// antd's own colours, matched to the brand tokens in index.css. Without this
// every Switch, Tabs ink bar, Pagination, Radio and primary Button stayed antd
// blue next to a violet app. Light algorithm as before; dark mode is still
// handled by styles/antd-overrides.css.
const ANTD_THEME = {
  token: {
    colorPrimary: "#8a45e6",
    colorLink: "#8a45e6",
    colorInfo: "#8a45e6",
    borderRadius: 8,
    fontFamily: "Inter, sans-serif",
  },
};

function App() {
  const [loading, setLoading] = useState(true);
  return (
    <ThemeProvider>
      <ConfigProvider theme={ANTD_THEME}>
        <SessionProvider>
          <ToastProvider>
            {loading ? <SplashScreen setLoading={setLoading} /> : <AppWrapper />}
          </ToastProvider>
        </SessionProvider>
      </ConfigProvider>
    </ThemeProvider>
  );
}

export default App;
